/**
 * useOfflineQueue — E6-S1
 *
 * React hook over src/utils/offline-queue.ts. Every read and write is bound to
 * a verified session-scoped `QueueAccess`. Callers either pass a ready
 * `access` object, or a `scope` with the current `sessionToken` and an
 * optional `canProcess` currency check, from which the hook builds one.
 * Undefined or stale access disables ALL queue access; it never means "all
 * scopes".
 *
 * Consumers get: `isOnline`, `pending` (queue snapshot), `error`, and
 * imperative `enqueue` / `flush` / `sweepSynced` helpers.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import {
  enqueue as qEnqueue,
  list as qList,
  drain as qDrain,
  deleteSynced as qDeleteSynced,
  isQueueAccessCurrent,
  type QueueAccess,
  type QueueItem,
} from '../utils/offline-queue';
import { getSessionGeneration, subscribeSession } from '../providers/cognito';

function subscribeOnline(callback: () => void): () => void {
  window.addEventListener('online', callback);
  window.addEventListener('offline', callback);
  return () => {
    window.removeEventListener('online', callback);
    window.removeEventListener('offline', callback);
  };
}
function getOnlineSnapshot(): boolean {
  return typeof navigator !== 'undefined' ? navigator.onLine : true;
}

export interface UseOfflineQueueOptions<T> {
  /** Verified session-bound access. Takes precedence over `scope`. */
  access?: QueueAccess;
  /** Verified tenant/actor/resource scope. Requires `sessionToken`. */
  scope?: string;
  /** Current signed session token that owns `scope`. */
  sessionToken?: string;
  /** Extra currency check evaluated with every queue operation. */
  canProcess?: () => boolean;
  worker: (payload: T, operationId: string, access: QueueAccess) => Promise<void>;
  /** When true (default), automatically drains the queue on reconnect. */
  autoFlushOnReconnect?: boolean;
  /**
   * When false, selecting a different scope loads its journal without
   * submitting it. An explicit flush or a real reconnect arms retries.
   */
  autoFlushOnScopeChange?: boolean;
  /** ms between manual poll refreshes of the queue snapshot. Default 1500. */
  pollIntervalMs?: number;
}

export interface UseOfflineQueueResult<T> {
  isOnline: boolean;
  pending: QueueItem<T>[];
  error?: string;
  enqueue: (payload: T, id?: string) => Promise<QueueItem<T>>;
  flush: () => Promise<{ attempted: number; synced: number; errored: number }>;
  sweepSynced: () => Promise<number>;
  refresh: () => Promise<void>;
}

export function useOfflineQueue<T = unknown>(
  opts: UseOfflineQueueOptions<T>,
): UseOfflineQueueResult<T> {
  const {
    autoFlushOnReconnect = true,
    autoFlushOnScopeChange = true,
    pollIntervalMs = 1500,
  } = opts;
  const latest = useRef(opts);
  latest.current = opts;
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const callerAccess = opts.access;
  const scope = callerAccess?.scope ?? opts.scope;
  const sessionToken = callerAccess?.sessionToken ?? opts.sessionToken;
  const access = useMemo<QueueAccess | undefined>(() => {
    if (callerAccess) return callerAccess;
    if (!scope || !sessionToken) return undefined;
    return {
      scope,
      sessionToken,
      isCurrent: () => latest.current.canProcess?.() ?? true,
    };
  }, [callerAccess, scope, sessionToken]);

  const isOnline = useSyncExternalStore(subscribeOnline, getOnlineSnapshot, () => true);
  const sessionGeneration = useSyncExternalStore(
    subscribeSession,
    getSessionGeneration,
    getSessionGeneration,
  );

  const [snapshot, setSnapshot] = useState<{ scope?: string; items: QueueItem<T>[] }>({ items: [] });
  const [error, setError] = useState<string>();
  const automaticScope = useRef<string | undefined>(undefined);
  const suppressedScope = useRef<string | undefined>(undefined);
  const previousTransition = useRef<{ scope?: string; online: boolean } | undefined>(undefined);

  const allowed = useCallback(
    () => mounted.current && isQueueAccessCurrent(access) &&
      (latest.current.access?.scope ?? latest.current.scope) === scope,
    [access, scope],
  );

  const refresh = useCallback(async (): Promise<void> => {
    if (!allowed()) {
      setSnapshot({ items: [] });
      return;
    }
    const generation = sessionGeneration;
    try {
      const items = await qList(access) as QueueItem<T>[];
      if (generation !== getSessionGeneration() || !allowed()) {
        setSnapshot({ items: [] });
        return;
      }
      setSnapshot({ scope, items });
      setError(undefined);
    } catch (err) {
      if (allowed()) {
        setError(err instanceof Error ? err.message : 'Local queue is unavailable');
      }
    }
  }, [access, allowed, scope, sessionGeneration]);

  const enqueue = useCallback(
    async (payload: T, id?: string) => {
      if (!allowed()) throw new Error('An authenticated capture scope is required');
      const item = await qEnqueue<T>(payload, { id, access });
      await refresh();
      return item;
    },
    [access, allowed, refresh],
  );

  const flush = useCallback(
    async (automatic = false) => {
      if (!allowed() || !getOnlineSnapshot()) {
        return { attempted: 0, synced: 0, errored: 0 };
      }
      if (!automatic) { automaticScope.current = scope; suppressedScope.current = undefined; }
      // Capture this scope's worker; do not replace it mid-drain on a user switch.
      const result = await qDrain<T>(latest.current.worker, access, { automatic });
      await refresh();
      return result;
    },
    [access, allowed, refresh, scope],
  );

  const sweepSynced = useCallback(async () => {
    if (!allowed()) return 0;
    const n = await qDeleteSynced(access);
    await refresh();
    return n;
  }, [access, allowed, refresh]);

  // Initial + periodic refresh so UI reflects background changes.
  useEffect(() => {
    let active = true;
    queueMicrotask(() => { if (active) void refresh(); });
    const timer = pollIntervalMs > 0
      ? window.setInterval(() => { void refresh(); }, pollIntervalMs)
      : undefined;
    return () => {
      active = false;
      if (timer !== undefined) window.clearInterval(timer);
    };
  }, [refresh, pollIntervalMs]);

  // Auto-flush on reconnect or scope selection. A bounded retry interval also
  // recovers an interrupted request without a synthetic offline/online
  // transition. Conflicts and blocked items are never automatically retried.
  useEffect(() => {
    const previous = previousTransition.current;
    previousTransition.current = { scope, online: isOnline };
    const scopeChanged = !!previous && previous.scope !== scope;
    const reconnected = !!previous && !previous.online && isOnline;
    if (scopeChanged) automaticScope.current = undefined;
    if (scopeChanged && !autoFlushOnScopeChange) suppressedScope.current = scope;
    if (reconnected || suppressedScope.current !== scope) suppressedScope.current = undefined;
    if (!autoFlushOnReconnect || !isOnline || !scope || !isQueueAccessCurrent(access)) return;
    let active = true;
    const run = (automatic: boolean) => {
      void flush(automatic).catch((err) => {
        if (active && allowed()) {
          setError(err instanceof Error ? err.message : 'Queue synchronization failed');
        }
      });
    };
    if (autoFlushOnScopeChange || suppressedScope.current !== scope) {
      queueMicrotask(() => { if (active) run(false); });
    }
    const retry = window.setInterval(() => {
      if (automaticScope.current === scope) run(true);
    }, 30000);
    return () => {
      active = false;
      window.clearInterval(retry);
    };
  }, [scope, isOnline, autoFlushOnReconnect, autoFlushOnScopeChange, flush, allowed, access, sessionGeneration]);

  const visible = allowed() && snapshot.scope === scope
    ? snapshot.items.filter((item) => item.bindingVersion === 1 && item.scope === scope)
    : [];

  return {
    isOnline,
    pending: visible,
    error,
    enqueue,
    flush,
    sweepSynced,
    refresh,
  };
}
